<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="modelValue"
        class="modal-overlay"
        @click.self="handleOverlayClick"
      >
        <div
          v-bind="$attrs"
          class="modal-container"
          :class="[`modal-${size}`]"
          role="dialog"
          aria-modal="true"
        >
          <!-- Header -->
          <div class="modal-header">
            <h2 class="modal-title">{{ title }}</h2>
            <button
              v-if="closable"
              class="modal-close"
              @click="close"
              aria-label="Fechar"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <slot></slot>
          </div>

          <!-- Footer -->
          <div v-if="$slots.footer" class="modal-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { watch, onUnmounted } from "vue";
import { openOverlay, closeOverlay, isTopOverlay } from "@/utils/overlayStack";

/* eslint-disable no-undef */
// Teleport root can't receive fallthrough attrs: forward them (data-test, class, ...) to the container.
defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "medium",
    validator: (v) => {
      const valid = ["small", "medium", "large", "full"];
      if (!valid.includes(v)) {
        // Loud failure pra evitar bug silencioso de modal full-screen
        // (causa raiz histórica: API divergente do EntityDrawer que usa sm/md/lg/xl).
        // eslint-disable-next-line no-console
        console.error(
          `[BaseModal] invalid size="${v}". Valid: ${valid.join("|")}. ` +
          `Sem CSS .modal-${v}, modal cai pra full-width. Veja docs/patterns/anti-patterns.md.`,
        );
      }
      return valid.includes(v);
    },
  },
  closable: {
    type: Boolean,
    default: true,
  },
  closeOnOverlay: {
    type: Boolean,
    default: true,
  },
  closeOnEscape: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

// Methods
function close() {
  emit("update:modelValue", false);
  emit("close");
}

function handleOverlayClick() {
  if (props.closeOnOverlay) {
    close();
  }
}

// Shared overlay stack: only the topmost overlay handles Escape (closing this modal does
// not close the drawer it was opened from), and the body scroll lock holds while any
// overlay is open — closing the modal no longer unlocks the page behind the drawer.
const overlayToken = Symbol("BaseModal");

function handleEscape(event) {
  if (
    event.key === "Escape" &&
    props.closeOnEscape &&
    props.modelValue &&
    isTopOverlay(overlayToken)
  ) {
    close();
  }
}

// Watch for escape key
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      openOverlay(overlayToken);
    } else {
      document.removeEventListener("keydown", handleEscape);
      closeOverlay(overlayToken);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  closeOverlay(overlayToken);
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: var(--bg-card);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%;
}

/* Sizes */
.modal-small {
  max-width: 400px;
}

.modal-medium {
  max-width: 600px;
}

.modal-large {
  max-width: 900px;
}

.modal-full {
  max-width: 95vw;
  max-height: 95vh;
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  margin: 0;
  font-size: var(--font-lg);
  font-weight: 600;
  color: var(--text-primary);
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--radius-full);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-base);
}

.modal-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

/* Body */
.modal-body {
  flex: 1;
  padding: var(--space-4);
  overflow-y: auto;
}

/* Inputs dentro do modal - garantir contraste */
.modal-body input,
.modal-body select,
.modal-body textarea {
  background: var(--bg-input) !important;
  color: var(--text-primary) !important;
  border-color: var(--border-color);
}

.modal-body input:focus,
.modal-body select:focus,
.modal-body textarea:focus {
  border-color: var(--accent-primary);
  outline: none;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

/* Footer */
.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-4);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

/* Mobile fix: em viewports ≤ 480px um footer com 3 botões (ex.: convite de
   instalação no iOS — Não + Lembre-me mais tarde + Sim, como faço?) não cabe
   na horizontal: os labels são uppercase e `white-space: nowrap`, então
   transbordam o card em vez de encolher. Stack vertical com botões
   full-width, mesmo padrão já usado no EntityDrawer (I-4). O `:deep` é
   necessário porque o conteúdo vem do slot. */
@media (max-width: 480px) {
  .modal-footer {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .modal-footer :deep(button) {
    width: 100%;
    justify-content: center;
  }
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(-20px);
}

/* Responsive */
@media (max-width: 767px) {
  .modal-overlay {
    padding: var(--space-2);
  }

  .modal-container {
    max-height: 95vh;
  }

  .modal-small,
  .modal-medium,
  .modal-large {
    max-width: 100%;
  }
}
</style>
